import React from 'react';
import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { propTypes } from '../../util/types';
import { resolveLatestProcessName, getProcess } from '../../transactions/transaction';
import { types as sdkTypes } from '../../util/sdkLoader';

const { Money } = sdkTypes;

import css from './OrderBreakdown.module.css';

/**
 * A component that renders the total price as a line item.
 *
 * @component
 * @param {Object} props
 * @param {propTypes.transaction} props.transaction - The transaction to render
 * @param {boolean} props.isProvider - Whether the provider is the one receiving the commission
 * @param {intlShape} props.intl - The intl object
 * @returns {JSX.Element}
 */
const LineItemTotalPrice = props => {
  const { transaction, isProvider, intl } = props;
  const processName = resolveLatestProcessName(transaction?.attributes?.processName);
  if (!processName) {
    return null;
  }
  const process = getProcess(processName);
  const isCompleted = process.isCompleted(transaction?.attributes?.lastTransition);
  const isRefunded = process.isRefunded(transaction?.attributes?.lastTransition);

  let providerTotalMessageId = 'OrderBreakdown.providerTotalDefault';
  if (isCompleted) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalReceived';
  } else if (isRefunded) {
    providerTotalMessageId = 'OrderBreakdown.providerTotalRefunded';
  }

  const totalPrice = isProvider
    ? transaction.attributes.payoutTotal
    : transaction.attributes.payinTotal;
  const formattedTotalPrice = formatMoney(intl, totalPrice);

  // Audit item 10: for renters, "Total price" quietly included the
  // refundable deposit, overstating the real cost. When a deposit line
  // is present, split the story: "Rental total" (what the rental
  // actually costs) above "Due today" (what the card is charged now,
  // deposit included — the deposit comes back after a clean return).
  // Only pre-payment views: once the booking completed or refunded,
  // "Due today" would be nonsense — those fall through to the plain
  // total row.
  const depositItem = !isProvider && !isCompleted && !isRefunded
    ? (transaction.attributes.lineItems || []).find(
        i => i.code === 'line-item/security-deposit' && !i.reversal
      )
    : null;
  const depositAmount = depositItem?.lineTotal?.amount || 0;

  if (!isProvider && depositAmount > 0 && totalPrice.amount >= depositAmount) {
    const rentalTotal = new Money(totalPrice.amount - depositAmount, totalPrice.currency);
    return (
      <>
        <hr className={css.totalDivider} />
        <div className={css.lineItem}>
          <span className={css.itemLabel}>
            <FormattedMessage id="OrderBreakdown.rentalTotal" />
          </span>
          <span className={css.itemValue}>{formatMoney(intl, rentalTotal)}</span>
        </div>
        <div className={css.lineItemTotal}>
          <div className={css.totalLabel}>
            <FormattedMessage id="OrderBreakdown.dueToday" />
            <span className={css.dueTodayNote}>
              <FormattedMessage
                id="OrderBreakdown.dueTodayNote"
                values={{ amount: formatMoney(intl, depositItem.lineTotal) }}
              />
            </span>
          </div>
          <div className={css.totalPrice}>{formattedTotalPrice}</div>
        </div>
      </>
    );
  }

  const totalLabel = isProvider ? (
    <FormattedMessage id={providerTotalMessageId} />
  ) : (
    <FormattedMessage id="OrderBreakdown.total" />
  );

  return (
    <>
      <hr className={css.totalDivider} />
      <div className={css.lineItemTotal}>
        <div className={css.totalLabel}>{totalLabel}</div>
        <div className={css.totalPrice}>{formattedTotalPrice}</div>
      </div>
    </>
  );
};

export default LineItemTotalPrice;
